import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { formatDateForAPI, transformGuestsForSearch } from "../../../utils/apiUtils";
import { Guest } from "../../../api/contracts/reservation";
import { fetchCategories } from "../../../api/api-hooks/category-ads";
import type {
  CategoryAdCot,
  CategoryAdExtraBeds,
} from "../../../api/contracts/category-ads";
import { LanguageCode } from "../../../api/contracts/language-settings";
import { useBookingCodeStore } from "../../../stores/bookingCodeStore";
import { sumPerUnit } from "./sumPerUnit";

/** The beds a unit's own party gets in the category, from the same priced response as its price. */
export type UnitBeds = {
  extraBeds: CategoryAdExtraBeds | null;
  cot: CategoryAdCot | null;
};

interface UseRoomInfoPricingParams {
  siteId: string;
  selectedLanguage: LanguageCode;
  categoryId?: string;
  fallbackPrice?: number;
  selectedStartDate: Date | null;
  selectedEndDate: Date | null;
  units: Array<{
    id: string;
    guests: Guest[];
  }>;
}

export const useRoomInfoPricing = ({
  siteId,
  selectedLanguage,
  categoryId,
  fallbackPrice,
  selectedStartDate,
  selectedEndDate,
  units,
}: UseRoomInfoPricingParams) => {
  const bookingCode = useBookingCodeStore((s) =>
    s.applied && s.code ? s.code : undefined,
  );
  const unitPricingInputs = useMemo(
    () =>
      units.map((unit) => {
        const adults = unit.guests.filter((guest) => guest.ageType === "adult").length;
        const teenagers = unit.guests.filter(
          (guest) => guest.ageType === "teenager",
        ).length;
        const { children: childGuests, infants: infantGuests } = splitGuestsByAgeType(
          unit.guests,
        );
        const transformedGuests = transformGuestsForSearch(childGuests, infantGuests);
        const guestMixKey = JSON.stringify({
          adults,
          teenagers,
          children: transformedGuests.children.map((guest) => ({
            age: guest.age,
            ageType: guest.ageType,
            bedCapacityType: guest.bedCapacityType,
          })),
          infants: transformedGuests.infants.map((guest) => ({
            age: guest.age,
            ageType: guest.ageType,
            bedCapacityType: guest.bedCapacityType,
          })),
        });
        return {
          unitId: unit.id,
          guestMixKey,
          adults,
          teenagers,
          children: transformedGuests.children,
          infants: transformedGuests.infants,
        };
      }),
    [units],
  );

  const uniqueMixes = useMemo(() => {
    const mixesByKey = new Map<
      string,
      {
        adults: number;
        teenagers: number;
        children: ReturnType<typeof transformGuestsForSearch>["children"];
        infants: ReturnType<typeof transformGuestsForSearch>["infants"];
      }
    >();

    unitPricingInputs.forEach((input) => {
      // Infants-only is never bookable, so it is neither priced nor displayed.
      if (
        input.infants.length > 0 &&
        input.adults + input.teenagers + input.children.length === 0
      )
        return;
      if (mixesByKey.has(input.guestMixKey)) return;
      mixesByKey.set(input.guestMixKey, {
        adults: input.adults,
        teenagers: input.teenagers,
        children: input.children,
        infants: input.infants,
      });
    });

    return Array.from(mixesByKey.entries()).map(([guestMixKey, guests]) => ({
      guestMixKey,
      ...guests,
    }));
  }, [unitPricingInputs]);

  const pricedMixQueries = useQueries({
    queries: uniqueMixes.map((mix) => ({
      queryKey: [
        "roomInfoPricedCategories",
        siteId,
        selectedLanguage,
        categoryId,
        selectedStartDate?.toISOString(),
        selectedEndDate?.toISOString(),
        mix.guestMixKey,
        bookingCode,
      ],
      enabled: !!siteId && !!categoryId && !!selectedStartDate && !!selectedEndDate,
      queryFn: () =>
        fetchCategories({
          siteId,
          languageCode: selectedLanguage,
          filter: "active",
          arrivalDate: formatDateForAPI(selectedStartDate!),
          departureDate: formatDateForAPI(selectedEndDate!),
          guests: {
            adults: mix.adults,
            teenagers: mix.teenagers,
            children: mix.children,
            infants: mix.infants,
          },
          ...(bookingCode ? { code: bookingCode } : {}),
        }),
      staleTime: 5000,
    })),
  });

  const priceByMixKey = useMemo(() => {
    const entries = uniqueMixes.map((mix, index) => {
      const pricedCategory = pricedMixQueries[index]?.data?.find(
        (candidate) => candidate.id === categoryId,
      );
      return [mix.guestMixKey, pricedCategory?.price] as const;
    });
    return new Map(entries);
  }, [categoryId, pricedMixQueries, uniqueMixes]);

  // The same response carries the pre-discount price when a booking code is
  // applied and the site opted into showing it. Summing it per unit alongside
  // `price` gives a comparable original for the footer strike — the dialog used
  // to infer one from the single-unit catalog price, which only held before the
  // live re-price and so was dropped in the normal case.
  const originalPriceByMixKey = useMemo(() => {
    const entries = uniqueMixes.map((mix, index) => {
      const pricedCategory = pricedMixQueries[index]?.data?.find(
        (candidate) => candidate.id === categoryId,
      );
      return [mix.guestMixKey, pricedCategory?.originalPrice] as const;
    });
    return new Map(entries);
  }, [categoryId, pricedMixQueries, uniqueMixes]);

  // Comes off the same priced response as the prices, so the badge can never
  // disagree with the figures it sits next to.
  const limitedAvailability = pricedMixQueries.some((query) =>
    query.data?.some(
      (candidate) => candidate.id === categoryId && candidate.limitedAvailability === true,
    ),
  );

  const unitBeds: Record<string, UnitBeds | undefined> = Object.fromEntries(
    unitPricingInputs.map((input) => {
      const index = uniqueMixes.findIndex(
        (mix) => mix.guestMixKey === input.guestMixKey,
      );
      const priced = pricedMixQueries[index]?.data?.find(
        (candidate) => candidate.id === categoryId,
      );
      return [
        input.unitId,
        priced
          ? { extraBeds: priced.extraBeds ?? null, cot: priced.cot ?? null }
          : undefined,
      ];
    }),
  );

  const unitCount = Math.max(units.length, 1);
  const fallbackSummaryPrice =
    typeof fallbackPrice === "number" ? fallbackPrice * unitCount : undefined;
  const hasPricePrerequisites =
    !!siteId && !!categoryId && !!selectedStartDate && !!selectedEndDate;

  // True while any guest-mix query is in flight (including background refetches
  // triggered by guest or date changes). Consumers use this to show a loader
  // and gate the confirm button until the price stabilizes.
  const isPricing =
    hasPricePrerequisites && pricedMixQueries.some((query) => query.isFetching);

  if (!hasPricePrerequisites) {
    return {
      summaryPrice: fallbackSummaryPrice,
      summaryOriginalPrice: undefined,
      isPricing: false,
      limitedAvailability: false,
      unitBeds: {} as Record<string, UnitBeds | undefined>,
    };
  }

  const guestMixKeys = unitPricingInputs.map((input) => input.guestMixKey);
  const summaryPrice = sumPerUnit(guestMixKeys, priceByMixKey, fallbackPrice);
  const summaryOriginalPrice = sumPerUnit(guestMixKeys, originalPriceByMixKey);

  return {
    summaryPrice,
    summaryOriginalPrice,
    isPricing,
    limitedAvailability,
    unitBeds,
  };
};

const splitGuestsByAgeType = (guests: Guest[]) => {
  const children = guests.filter((guest) => guest.ageType === "child");
  const infants = guests.filter((guest) => guest.ageType === "infant");
  return { children, infants };
};
